import { Button, Divider, Link, List, ListItem, Stack, Typography, useTheme } from "@mui/material";
import { FC, ReactNode } from "react";
import ScreenBanner from "../components/screenBanner";
import faqBannerImage from "../assets/8bit.jpg";
import AttributionLanguage from "../components/attributionLanguage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScreenBase from "./ScreenBase";

type Props = {
	question: string;
	answer: ReactNode;
};

const FaqScreen: FC = () => {
	const theme = useTheme();

	const phoneNumber = '+19415677121';
	const phoneNumberFormatted = '(941) 567-7121';

	const qas: Props[] = [
		{
			question: 'How should I properly credit your music in my videos?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">
						Great question! You can just copy/paste the attribution language in the download box when you grab my
						music. If you forgot to do this and need to grab it again, it's very simple:
					</Typography>
					<AttributionLanguage maxWidth={520} variant="elevation" />
				</Stack>
			)
		},
		{
			question: 'Where can I use your music?',
			answer: 'Pretty much anywhere!  You just need to give attribution to me or in some cases purchase an extended license.'
		},
		{
			question: 'Do I need to sign up or have a subscription to use these tracks?',
			answer: `Absolutely not! Just use the search tool on the tunes page to find  the track you're looking for and download the track. The attribution language is in the download dialogue box to copy/paste into your video description or equivalent context.`
		},
		{
			question: 'Is it REALLY FREE to use!?',
			answer: 'Yes it is - when you give me attribution in your content!'
		},
		{
			question: 'Can I monetize a channel and still use your music?',
			answer: `Most definitely, yes. Just be sure to give me proper attribution as some of the platforms look for my name, song title, and/or web address when they detect my music to ensure that I'm getting proper attribution, and that someone else isn't claiming my music.`
		},
		{
			question: 'Can I edit, trim, or adjust the speed of your music to fit my video or project?',
			answer: 'You sure can!'
		},
		{
			question: 'May I use one of your tracks and sing over it and publish it - and how should I credit you?',
			answer: `I'm flattered that you like my music enough to add lyrics to it! If you publish it anywhere that collects royalties, you just have to co-publish with me to ensure any royalties are properly disbursed between us. Check with whomever you publish your music with for clarification on how to do this.`
		},
		{
			question: 'Are you registered with YouTube Content ID?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">
						Yes - to protect content creators like you! I did this so no one else can register my music and claim
						to be me or the author of the music. If you give me attribution properly in your video description,
						this registration has no effect on you or your content. If you have trouble or get a content ID claim,
						see this page to clear it:
					</Typography>
				</Stack>
			)
		},
		{
			question: 'I received a copyright claim from "We Are Era Music" - what the heck is this?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1" color="textPrimary">This is a YouTube Content ID copyright claim and happened because:</Typography>
					<List component="ol" sx={{ listStyleType: 'decimal', pl: 2 }}>
						<ListItem sx={{ display: 'list-item' }}><Typography variant="body1">You didn't properly attribute the music to me in your video (see above)</Typography></ListItem>
						<ListItem sx={{ display: 'list-item' }}><Typography variant="body1">You misspelled my name or website in your attribution language</Typography></ListItem>
						<ListItem sx={{ display: 'list-item' }}><Typography variant="body1">Something else - but still had to do with number 1 above</Typography></ListItem>
						<ListItem sx={{ display: 'list-item' }}><Typography variant="body1">See above for the link on how to fix this problem, or reach out to me at <Link href="mailto:timkuligfreemusic@gmail.com">timkuligfreemusic@gmail.com</Link> for help. :)</Typography></ListItem>
					</List>
				</Stack>
			)
		},
		{
			question: 'Is this music “Copyright Free”?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">NO. I own the copyright to all of my music.</Typography>
					<Typography variant="body1">
						The music is free to use with attribution. There's a common misunderstanding between the definitions
						of “Copyright Free” and “Royalty Free”, and to this day it's an endless battle for music creators to
						clarify.
					</Typography>
				</Stack>
			)
		},
		{
			question: 'Is this music in the Public Domain?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">NO. The music is not in the Public Domain.</Typography>
					<Typography variant="body1">
						I do, however, offer it to creatives like yourselves FOR FREE when you give proper attribution to
						me in your content, videos, movies, TV, radio, podcasts, etc.
					</Typography>
				</Stack>
			)
		},
		{
			question: 'You give it all away!  How can I support you?',
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">
						Phenomenal question, and much appreciated! You can join my Patreon for as little as $1 per month to
						help support my work. You can also donate directly via PayPal.
					</Typography>
					<Stack direction="row" spacing={2}>
						<Button
							variant="outlined"
							size="small"
							startIcon={<FontAwesomeIcon icon={{ prefix: 'fab', iconName: 'patreon' }} style={{ fontSize: 14, opacity: 0.8 }} />}
							href="https://www.patreon.com/timkulig?fan_landing=true"
							target="_blank"
						>
							<Typography variant="button">Patreon</Typography>
						</Button>
						<Button
							variant="outlined"
							size="small"
							startIcon={<FontAwesomeIcon icon={{ prefix: 'fab', iconName: 'paypal' }} style={{ fontSize: 14, opacity: 0.8 }} />}
							href="https://paypal.me/timkuligfreemusic"
							target="_blank"
						>
							<Typography variant="button">PayPal</Typography>
						</Button>
					</Stack>
				</Stack>
			)
		},
		{
			question: 'Do you do commission work?',
			answer: (
				<Typography variant="body1">
					I spend most of my time writing a TON of music in many different genres. That said, if you're looking for
					something specific or have a project you'd like to hire me for, I can do that as well. Reach out to me at{' '}
					<Link href="mailto:timkuligfreemusic@gmail.com">timkuligfreemusic@gmail.com</Link> or text me at{' '}
					<Link href={`sms:${phoneNumber}`}>{phoneNumberFormatted}</Link> to start the conversation!
				</Typography>

			)
		},
		{
			question: `I still haven't found what I'm looking for (not quoting U2 here) - can you help me?`,
			answer: (
				<Stack spacing={2}>
					<Typography variant="body1">
						If you're having an existential crisis I might not be the right person to talk to.
					</Typography>
					<Typography variant="body1">
						That said, if you are struggling to find the right sonic background for your narrative and would like
						some guidance or are just having difficulty finding the right thing, I can certainly lend a hand and
						try and help you out. Shoot me an email at <Link href="mailto:timkuligfreemusic@gmail.com">timkuligfreemusic@gmail.com</Link>
						and I'll see what I can do!
					</Typography>
				</Stack>
			)
		}
	];

	const Question: FC<Props> = ({ question, answer }) => (
		<Stack spacing={4}>
			<Stack spacing={2}>
				<Stack direction="row" spacing={3} sx={{ alignItems: 'baseline' }}>
					<Typography variant="h6" color={theme.palette.primary.main} fontWeight="bold">Q</Typography>
					<Typography variant="body1" color={theme.palette.grey[400]} fontWeight="bold">{question}</Typography>
				</Stack>
				<Stack direction="row" spacing={3} sx={{ alignItems: 'baseline' }}>
					<Typography variant="h6" color={theme.palette.secondary.main} fontWeight="bold">A</Typography>
					<Typography component="div" variant="body1" color={theme.palette.text.primary}>
						{answer}
					</Typography>
				</Stack>
			</Stack>
			<Divider />
		</Stack>
	);

	return (
		<ScreenBase
			fixedChildren={(
				<ScreenBanner
					title="Frequently Asked Questions"
					bannerImage={faqBannerImage}
					height={120}
					verticalAdjust="25%"
					brightnessAdjust={0.75}
				/>
			)}
			scrollableChildren={(
				<Stack spacing={4} sx={{ maxWidth: '70ch' }}>
					{qas.map((qa, index) => (
						<Question key={index} question={qa.question} answer={qa.answer} />
					))}
				</Stack>
			)}
		/>
	);
};

export default FaqScreen;